// File: frontend/src/services/api.js

import axios from 'axios';

export const getApiBaseUrl = () => {
  // 1. Local React development server (port 3000 -> backend on 5000)
  if (typeof window !== 'undefined' && window.location && window.location.port === '3000') {
    return 'http://localhost:5000/api';
  }

  // 2. Runtime window override (e.g. from index.html or dynamic config)
  if (typeof window !== 'undefined' && window.__API_URL__) {
    return window.__API_URL__.replace(/\/+$/, '');
  }

  // 2. localStorage override for easy debugging/switching
  if (typeof window !== 'undefined') {
    try {
      const override = localStorage.getItem('API_URL_OVERRIDE');
      if (override) return override.replace(/\/+$/, '');
    } catch (e) {}
  }

  // 3. Build-time environment variable (if non-empty and not localhost when deployed)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/+$/, '');
  }

  // 4. Local React development server (port 3000 -> backend on 5000)
  if (typeof window !== 'undefined' && window.location && window.location.port === '3000') {
    return 'http://localhost:5000/api';
  }

  // 5. In deployed environment, default to same domain /api
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return `${window.location.origin}/api`;
  }

  return 'http://localhost:5000/api';
};

export const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) => {
    return api.post('/auth/login', { username, password }).then(res => res.data);
  },

  studentLogin: (username, password) => {
    return api.post('/auth/student/login', { username, password }).then(res => res.data);
  },

  forgotPassword: (username) => {
    return api.post('/auth/forgot-password', { username }).then(res => res.data);
  },

  changePassword: (currentPassword, newPassword) => {
    return api.post('/auth/change-password', { currentPassword, newPassword }).then(res => res.data);
  },
  
  setToken: (token) => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }
};

// Users API
export const usersAPI = {
  getAll: () => {
    return api.get('/users').then(res => res.data);
  },
  
  create: (userData) => {
    return api.post('/users', userData).then(res => res.data);
  },
  
  update: (id, userData) => {
    return api.put(`/users/${id}`, userData).then(res => res.data);
  },
  
  delete: (id) => {
    return api.delete(`/users/${id}`).then(res => res.data);
  }
};

// Classes API
export const classesAPI = {
  getAll: () => {
    return api.get('/classes').then(res => res.data);
  },
  
  get: (id) => {
    return api.get(`/classes/${id}`).then(res => res.data);
  },
  
  create: (classData) => {
    return api.post('/classes', classData).then(res => res.data);
  },
  
  update: (id, classData) => {
    return api.put(`/classes/${id}`, classData).then(res => res.data);
  },
  
  delete: (id) => {
    return api.delete(`/classes/${id}`).then(res => res.data);
  },

  setActive: (id, isActive) => {
    return api.patch(`/classes/${id}/active`, { is_active: isActive }).then(res => res.data);
  },

  bulkDelete: (classIds) => {
    return api.post('/classes/bulk-delete', { class_ids: classIds }).then(res => res.data);
  },
  
  getStudents: (id) => {
    return api.get(`/classes/${id}/students`).then(res => res.data);
  },
  
  addStudent: (classId, studentData) => {
    return api.post(`/classes/${classId}/students`, studentData).then(res => res.data);
  },

  getMaterials: (classId) => {
    return api.get(`/materials/class/${classId}`).then(res => res.data);
  },
  
  addMaterial: (materialData) => {
    return api.post('/materials', materialData).then(res => res.data);
  },

  updateMaterial: (materialId, materialData) => {
    return api.put(`/materials/${materialId}`, materialData).then(res => res.data);
  },

  deleteMaterial: (materialId) => {
    return api.delete(`/materials/${materialId}`);
  },
  
  removeStudent: (classId, studentId) => {
    return api.delete(`/classes/${classId}/students/${studentId}`).then(res => res.data);
  },

  removeStudentsBulk: (classId, studentIds) => {
    return api.post(`/classes/${classId}/students/remove-bulk`, { student_ids: studentIds }).then(res => res.data);
  },

  addStudents: (classId, studentIds) => {
    return api.post(`/classes/${classId}/students`, { student_ids: studentIds }).then(res => res.data);
  },

  batchEnroll: (classIds, studentIds) => {
    return api.post('/classes/batch-enroll', { class_ids: classIds, student_ids: studentIds }).then(res => res.data);
  },

  scheduleMakeup: (makeupData) => {
    return api.post('/classes/schedule-makeup', makeupData).then(res => res.data);
  },

  cancelMakeup: (classId) => {
    return api.delete(`/classes/${classId}/makeup`).then(res => res.data);
  }
};

// Students Portal API
export const studentPortalAPI = {
  getMyClasses: () => {
    return api.get('/student/my-classes').then(res => res.data);
  },
  
  getMyAttendance: () => {
    return api.get('/student/my-attendance').then(res => res.data);
  },

  getMyMaterials: () => {
    return api.get('/student/my-materials').then(res => res.data);
  },

  getMyNotes: () => {
    return api.get('/student/my-notes').then(res => res.data);
  },

  acknowledgeNote: (id) => {
    return api.put(`/student/my-notes/${id}/acknowledge`).then(res => res.data);
  },

  getMyAttendanceHistory: (params) => {
    return api.get('/student/my-attendance-history', { params }).then(res => res.data);
  },

  getMyPayments: () => {
    return api.get('/student/my-payments').then(res => res.data);
  },

  getMyProfile: () => {
    return api.get('/student/my-profile').then(res => res.data);
  },

  updateMyProfile: (data) => {
    return api.put('/student/my-profile', data).then(res => res.data);
  },

  changeMyPassword: (currentPassword, newPassword) => {
    return api.post('/student/change-password', { currentPassword, newPassword }).then(res => res.data);
  },
};

// Students API
export const studentsAPI = {
  getAll: () => {
    return api.get('/students').then(res => res.data);
  },

  getCramSchools: () => {
    return api.get('/students/cram-schools').then(res => res.data);
  },

  get: (id) => {
    return api.get(`/students/${id}`).then(res => res.data);
  },
  
  create: (studentData) => {
    return api.post('/students', studentData).then(res => res.data);
  },

  bulkImport: (students) => {
    return api.post('/students/bulk', { students }).then(res => res.data);
  },
  
  update: (id, studentData) => {
    return api.put(`/students/${id}`, studentData).then(res => res.data);
  },

  makeRegular: (studentId, classId) => {
    return api.post(`/students/${studentId}/make-regular`, { class_id: classId })
  },
  
  continueTrial: (studentId) => {
    return api.post(`/students/${studentId}/continue-trial`, {})
  },
  
  delete: (id) => {
    return api.delete(`/students/${id}`).then(res => res.data);
  },

  bulkDelete: (studentIds) => {
    return api.post('/students/bulk-delete', { student_ids: studentIds }).then(res => res.data);
  }
};

// Attendance API
export const attendanceAPI = {
  get: (classId, date) => {
    return api.get('/attendance', { 
      params: { class_id: classId, date } 
    }).then(res => res.data);
  },
  
  record: (attendanceData) => {
    return api.post('/attendance', attendanceData).then(res => res.data);
  },
  
  recordBulk: (classId, attendanceData, date) => {
    return api.post('/attendance/bulk', { 
      class_id: classId, 
      attendance_data: attendanceData, 
      date: date
    }).then(res => res.data);
  },

  getByDate: (params) => {
    return api.get('/attendance/by-date', { params }).then(res => res.data);
  },

  recordBulkClasses: (data) => {
    return api.post('/attendance/bulk-classes', data).then(res => res.data);
  },

  cancelSession: (classId, date) => api.post('/attendance/session/cancel', { 
      class_id: classId, 
      date: date 
  }).then(res => res.data),

  recordCramSession: (data) => api.post('/attendance/cram-session', data).then(res => res.data),

  deleteRecord: (attendanceId) => api.delete(`/attendance/${attendanceId}`).then(res => res.data),
};

// Reports API
export const reportsAPI = {
  generateCSV: (params) => {
    return api.get('/reports/attendance/csv', { 
      params,
      responseType: 'blob'
    }).then(res => res.data);
  },
  
  generatePDF: (params) => {
    return api.get('/reports/attendance/pdf', { 
      params,
      responseType: 'blob'
    }).then(res => res.data);
  },
  
  emailReport: (emailData) => {
    return api.post('/reports/attendance/email', emailData).then(res => res.data);
  },

  getStudentHistory: (studentId) => {
    return api.get(`/reports/student/${studentId}/history`).then(res => res.data);
  },

  getSummary: () => {
    return api.get('/reports/summary').then(res => res.data);
  },

  getStats: () => {
    return api.get('/reports/stats').then(res => res.data);
  },

  getTrialCount: () => {
    return api.get('/reports/stats/trial-count').then(res => res.data);
  },

  getMostAbsent: () => {
    return api.get('/reports/stats/most-absent').then(res => res.data);
  },

  getSuperAdminStats: (params) => {
    return api.get('/reports/super-admin-stats', { params }).then(res => res.data);
  }
};

// Rooms API
export const roomsAPI = {
  getAll: () => api.get('/rooms').then(res => res.data),
  create: (roomData) => api.post('/rooms', roomData).then(res => res.data),
  update: (id, roomData) => api.put(`/rooms/${id}`, roomData).then(res => res.data),
  delete: (id) => api.delete(`/rooms/${id}`).then(res => res.data),
};

// Logs API 
export const logsAPI = {
  getForClass: (classId) => api.get(`/logs/class/${classId}`).then(res => res.data),
  create: (logData) => api.post('/logs', logData).then(res => res.data),
  getPending: () => api.get('/logs/pending').then(res => res.data),
  review: (id, action) => api.put(`/logs/${id}/review`, { action }).then(res => res.data),
};

// Payroll API
export const payrollAPI = {
  generate: (params) => api.get('/payroll/generate', { params }).then(res => res.data),

  generatePayslip: (params) => api.get('/payroll/payslip', {
      params,
      responseType: 'blob' // Important: We expect a file, not JSON
  }).then(res => res.data),

  getMonthlyTrend: (params) => api.get('/payroll/monthly-trend', { params }).then(res => res.data),
};

// Expenses API
export const expensesAPI = {
  getMyExpenses: () => api.get('/expenses/my-expenses').then(res => res.data),
  getAll: () => api.get('/expenses/all').then(res => res.data),
  create: (expenseData) => api.post('/expenses', expenseData).then(res => res.data),
  updateStatus: (id, status) => api.put(`/expenses/${id}/status`, { status }).then(res => res.data),
};

// Our own backend always returns { error: "a string" }, but a file upload
// error can come directly from Cloudinary instead, which nests it as
// { error: { message: "..." } } - passing that object straight to
// toast.error() crashes the page (React can't render an object as a
// child), so callers should use this instead of reading err.response.data.error directly.
export function getUploadErrorMessage(err, fallback) {
  const apiError = err?.response?.data?.error;
  if (typeof apiError === 'string') return apiError;
  if (apiError?.message) return apiError.message;
  return err?.message || fallback;
}

// File Upload API
export const fileUploadAPI = {
    async upload(file) {
        // 1. Get the signature from our backend
        const { signature, timestamp } = await api.get('/uploads/signature').then(res => res.data);
        
        // 2. Create a FormData object to send the file
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY); // You'll add this to your .env
        formData.append('timestamp', timestamp);
        formData.append('signature', signature);

        // 3. Make the POST request directly to Cloudinary
        const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME; // You'll add this
        const url = `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`;
        
        const response = await axios.post(url, formData);
        
        // 4. Return the URL of the uploaded file
        return response.data.secure_url;
    }
};

// Lesson Fees & Taiwan Billing API
export const feesAPI = {
  getClassFees: (classId, date) => api.get(`/fees/class/${classId}/date/${date}`).then(res => res.data),
  confirmPayment: (data) => api.post('/fees/confirm-payment', data).then(res => res.data),
  confirmCash: (data) => api.post('/fees/confirm-cash', data).then(res => res.data),
  confirmCycle: (data) => api.post('/fees/confirm-cycle', data).then(res => res.data),
  getLedger: (params) => api.get('/fees/ledger', { params }).then(res => res.data),
};

// Bank Accounts (Taiwan ATM / Transfer) API
export const bankAccountsAPI = {
  getAll: () => api.get('/bank-accounts').then(res => res.data),
  create: (data) => api.post('/bank-accounts', data).then(res => res.data),
  update: (id, data) => api.put(`/bank-accounts/${id}`, data).then(res => res.data),
  setPrimary: (id) => api.post(`/bank-accounts/${id}/set-primary`).then(res => res.data),
  delete: (id) => api.delete(`/bank-accounts/${id}`).then(res => res.data),
};

// Leave Requests & Make-Up Class (請假與補課) API
export const leaveRequestsAPI = {
  getAll: (params) => api.get('/leave-requests', { params }).then(res => res.data),
  create: (data) => api.post('/leave-requests', data).then(res => res.data),
  updateStatus: (id, data) => api.put(`/leave-requests/${id}/status`, data).then(res => res.data),
  scheduleMakeup: (id, makeupData) => api.put(`/leave-requests/${id}/makeup`, makeupData).then(res => res.data),
  delete: (id) => api.delete(`/leave-requests/${id}`).then(res => res.data),
};

// LINE Messaging Integration API
export const lineAPI = {
  getSettings: () => api.get('/line/settings').then(res => res.data),
  saveSettings: (data) => api.post('/line/settings', data).then(res => res.data),
  sendTest: (target_line_id) => api.post('/line/test', { target_line_id }).then(res => res.data),
};

// LINE Pay API
export const linePayAPI = {
  getSettings: () => api.get('/line-pay/settings').then(res => res.data),
  saveSettings: (data) => api.post('/line-pay/settings', data).then(res => res.data),
  testConnection: (data) => api.post('/line-pay/test-connection', data).then(res => res.data),
  getInfo: () => api.get('/line-pay/info').then(res => res.data),
  requestPayment: (data) => api.post('/line-pay/request-payment', data).then(res => res.data),
  confirmPayment: (data) => api.post('/line-pay/confirm-payment', data).then(res => res.data),
  checkPaymentStatus: (transactionId) => api.get(`/line-pay/status/${transactionId}`).then(res => res.data),
};

export default api;